import { MultiSelect } from '@/components/multi-select'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Attendant } from '@/interfaces/attendant'
import { listAttendantsQuery } from '@/queries/attendant.query'
import { listDepartmentsQuery } from '@/queries/department.query'
import { AttendantService } from '@/services/attendant.service'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentRow?: Attendant
}

const formSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório.'),
  email: z.string().email('Email inválido.'),
  phone: z.string().optional(),
  departmentIds: z.array(z.string()).min(1, 'Departamento é obrigatório.'),
})

type AttendantForm = z.infer<typeof formSchema>

export function AttendantsMutateDrawer({ open, onOpenChange, currentRow }: Props) {
  const isUpdate = !!currentRow
  const queryClient = useQueryClient()
  const { data: departments } = useSuspenseQuery(listDepartmentsQuery)

  const form = useForm<AttendantForm>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: currentRow?.user.name ?? '',
      email: currentRow?.user.email ?? '',
      phone: currentRow?.user.phone ?? '',
      departmentIds: currentRow?.departments.map((department) => String(department.id)) ?? [],
    },
  })

  const onSubmit = form.handleSubmit((data) => {
    const payload = {
      ...data,
      name: data.name.trim(),
      departmentIds: data.departmentIds.map(Number),
    }

    const mutation = isUpdate && currentRow
      ? AttendantService.update(currentRow.id, payload)
      : AttendantService.create(payload)

    toast.promise(mutation, {
      loading: 'Salvando atendente...',
      success: () => {
        onOpenChange(false)
        form.reset()
        return isUpdate ? 'Atendente atualizado' : 'Atendente criado'
      },
      error: () => {
        return isUpdate ? 'Ocorreu um erro ao tentar atualizar o atendente.' : 'Ocorreu um erro ao tentar criar o atendente.'
      },
      finally: () => {
        queryClient.invalidateQueries({ queryKey: listAttendantsQuery.queryKey })
      },
    })
  })

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v)
        form.reset()
      }}
    >
      <SheetContent className='flex flex-col'>
        <SheetHeader className='text-left'>
          <SheetTitle>{isUpdate ? 'Atualizar' : 'Adicionar'} Atendente</SheetTitle>
          <SheetDescription>
            {isUpdate
              ? 'Atualize o atendente provendo as informações necessárias.'
              : 'Adicione um novo atendente provendo as informações necessárias.'}
            Clique em salvar quando estiver pronto.
          </SheetDescription>
        </SheetHeader>
        <Form {...form}>
          <form
            id='attendants-form'
            onSubmit={onSubmit}
            className='flex-1 space-y-5'
          >
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input {...field} autoFocus placeholder='Digite o nome do atendente' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='Digite o email do atendente' type="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='phone'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Telefone</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='Digite o telefone do atendente' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='departmentIds'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>Departamento</FormLabel>
                  <MultiSelect
                    placeholder="Selecione os departamentos"
                    options={departments.map(department => ({
                      label: department.name,
                      value: department.id.toString()
                    }))}
                    onValueChange={field.onChange}
                    defaultValue={field.value || []}
                    value={field.value || []}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        <SheetFooter className='gap-2'>
          <SheetClose asChild>
            <Button variant='outline'>Cancelar</Button>
          </SheetClose>
          <Button form='attendants-form' type='submit'>
            {isUpdate ? 'Atualizar' : 'Adicionar'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
} 